<template>
  <transition name="pwa-notice-slide">
    <div v-if="showUpdateNotice" class="pwa-update-backdrop">
      <aside
        class="pwa-notice pwa-notice--update"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="pwa-update-title">
        <div class="pwa-notice__content">
          <div class="pwa-notice__eyebrow">{{updateEyebrow}}</div>
          <div id="pwa-update-title" class="pwa-notice__title">{{updateTitle}}</div>
          <div class="pwa-notice__message">{{updateMessage}}</div>
          <div v-if="state.updateError" class="pwa-notice__error" aria-live="polite">{{state.updateError}}</div>
        </div>
        <div class="pwa-notice__actions">
          <button type="button" class="pink-button pwa-notice__primary" :disabled="state.updating" @click="updateNow">
            <span v-if="state.updating"><i>Updating...</i></span>
            <span v-else>{{state.updateError ? 'Retry update' : 'Update now'}}</span>
          </button>
          <button
            v-if="state.updateRecoveryAvailable"
            type="button"
            class="pwa-notice__later"
            :disabled="state.updating"
            @click="continueWithoutUpdate">
            Continue for now
          </button>
        </div>
      </aside>
    </div>

    <aside v-else-if="showInstallNotice" class="pwa-notice pwa-notice--install" role="status">
      <div class="pwa-notice__content">
        <div class="pwa-notice__eyebrow">Take the game with you</div>
        <div class="pwa-notice__title">Install Sorority Wars for faster, app-style access.</div>
      </div>
      <div class="pwa-notice__actions">
        <button type="button" class="pink-button pwa-notice__primary" :disabled="installing" @click="install">
          <span v-if="installing"><i>Opening...</i></span>
          <span v-else>Install</span>
        </button>
        <button type="button" class="pwa-notice__later" :disabled="installing" @click="dismissInstall">
          Not now
        </button>
      </div>
    </aside>
  </transition>
</template>

<script>
import pwaManager from '@/registerServiceWorker'

export default {
  name: 'PwaStatusNotices',
  data () {
    return {
      state: pwaManager.state,
      installing: false
    }
  },
  computed: {
    showUpdateNotice () {
      return this.state.updateAvailable && !this.state.updateDeferred
    },
    updateEyebrow () {
      return this.state.updateRecoveryAvailable ? 'Update needs another try' : 'App update required'
    },
    updateTitle () {
      return this.state.updateRecoveryAvailable
        ? 'The latest version is still waiting.'
        : 'A new version of Sorority Wars is ready.'
    },
    updateMessage () {
      if (this.state.updateRecoveryAvailable) {
        return 'The update did not finish cleanly. Try again, or continue playing for now.'
      }
      return 'Update now to continue with the latest version of the game.'
    },
    showInstallNotice () {
      return this.$route.name !== 'Download' &&
        !this.state.updateAvailable &&
        this.state.installable &&
        !this.state.installed &&
        this.state.now >= this.state.installDismissedUntil
    }
  },
  methods: {
    async install () {
      this.installing = true
      try {
        await pwaManager.install()
      } finally {
        this.installing = false
      }
    },
    dismissInstall () {
      pwaManager.dismissInstall()
    },
    updateNow () {
      pwaManager.applyUpdate()
    },
    continueWithoutUpdate () {
      pwaManager.continueWithoutUpdate()
    }
  }
}
</script>

<style scoped>
.pwa-notice {
  position: fixed;
  z-index: 1000000000;
  right: 10px;
  bottom: 10px;
  left: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: calc(100% - 20px);
  max-width: 680px;
  box-sizing: border-box;
  margin: 0 auto;
  padding: 10px;
  color: #000;
  background: #fff;
  border: 2px solid #e84063;
  border-radius: 8px;
  box-shadow: 0 4px 14px rgba(14, 85, 119, 0.28);
  text-align: left;
}

.pwa-update-backdrop {
  position: fixed;
  z-index: 1000000000;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  padding: max(10px, env(safe-area-inset-top)) max(10px, env(safe-area-inset-right)) max(10px, env(safe-area-inset-bottom)) max(10px, env(safe-area-inset-left));
  background: rgba(11, 90, 168, 0.55);
  overflow-y: auto;
}

.pwa-notice--update {
  position: relative;
  right: auto;
  bottom: auto;
  left: auto;
  width: 100%;
  max-width: 540px;
  max-height: calc(100dvh - 20px);
  margin: auto;
  border-color: #238cbd;
  overflow-y: auto;
}

.pwa-notice__content {
  min-width: 0;
}

.pwa-notice__eyebrow {
  color: #ad1f51;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.pwa-notice--update .pwa-notice__eyebrow {
  color: #0b5aa8;
}

.pwa-notice__title {
  margin-top: 2px;
  font-size: 14px;
  font-weight: 800;
}

.pwa-notice__message {
  margin-top: 4px;
  font-size: 11px;
  line-height: 1.4;
}

.pwa-notice__error {
  margin-top: 3px;
  color: #ad1f51;
  font-size: 11px;
  font-weight: 700;
}

.pwa-notice__actions {
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  gap: 6px;
}

.pwa-notice__primary,
.pwa-notice__later {
  min-height: 40px;
  padding: 7px 12px;
  font-family: inherit;
}

.pwa-notice__later {
  color: #0b5aa8;
  background: #d1ebf1;
  border: 1px solid #238cbd;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 800;
  cursor: pointer;
}

.pwa-notice button:disabled {
  cursor: wait;
  opacity: 0.65;
}

.pwa-notice-slide-enter-active,
.pwa-notice-slide-leave-active {
  transition: transform 250ms ease, opacity 250ms ease;
}

.pwa-notice-slide-enter,
.pwa-notice-slide-leave-to {
  opacity: 0;
  transform: translateY(12px);
}

@media only screen and (max-width: 599px) {
  .pwa-notice {
    align-items: stretch;
    flex-direction: column;
  }

  .pwa-notice__content {
    text-align: center;
  }

  .pwa-notice__actions {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .pwa-notice--update .pwa-notice__actions {
    grid-template-columns: 1fr;
  }

  .pwa-notice__primary,
  .pwa-notice__later {
    width: 100%;
  }
}
</style>
